/**
 * Small shared UI behaviour: back-to-top button, floating cart button (small screens),
 * reveal-on-scroll for product cards.
 */

(() => {
  const topButton = document.querySelector('[data-kvs-top]');

  if (topButton) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // The footer has its own back-to-top link; while it is on screen the floating button steps aside,
    // so two arrows never stack up (and the floating one never covers the company details).
    const footerTop = document.querySelector('.footer__to-top');
    let footerTopOnScreen = false;
    let frame = 0;

    const update = () => {
      frame = 0;
      topButton.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.8 && !footerTopOnScreen);
    };

    if (footerTop && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        footerTopOnScreen = entries.some((entry) => entry.isIntersecting);
        if (!frame) frame = requestAnimationFrame(update);
      }).observe(footerTop);
    }

    window.addEventListener(
      'scroll',
      () => {
        if (!frame) frame = requestAnimationFrame(update);
      },
      { passive: true },
    );

    topButton.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    });

    topButton.hidden = false;
    update();
  }
})();

// Floating cart: shown on small screens once the page is scrolled and the cart has items.
(() => {
  const fab = document.querySelector('[data-kvs-fab]');
  // The phone navigation already owns the single visible cart shortcut.
  if (!fab || document.querySelector('kvs-mobile-nav')) return;

  const small = window.matchMedia('(max-width: 749px)');
  const counter = fab.querySelector('[data-kvs-fab-count]');
  let count = Number(fab.dataset.count) || 0;
  let frame = 0;

  const update = () => {
    frame = 0;
    const drawerOpen = document.body.classList.contains('overflow-hidden');
    const visible = small.matches && count > 0 && window.scrollY > 320 && !drawerOpen;
    fab.classList.toggle('is-visible', visible);
    document.documentElement.classList.toggle('has-kvs-fab', visible);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };

  const setCount = (next) => {
    const changed = next !== count;
    count = next;
    counter.textContent = count > 99 ? '99+' : String(count);
    fab.setAttribute('aria-label', fab.dataset.label.replace('__COUNT__', count));
    if (changed && count > 0) {
      fab.classList.remove('is-bumped');
      void fab.offsetWidth;
      fab.classList.add('is-bumped');
    }
    schedule();
  };

  const refresh = () => {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Cart unavailable'))))
      .then((cart) => setCount(Number(cart.item_count) || 0))
      .catch(() => {});
  };

  fab.addEventListener('click', () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open(fab);
    else window.location.assign((window.routes && window.routes.cart_url) || '/cart');
  });

  window.addEventListener('scroll', schedule, { passive: true });
  small.addEventListener('change', schedule);
  // The drawers lock body scrolling with this class; hide the button while one is open.
  new MutationObserver(schedule).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('pageshow', (event) => event.persisted && refresh());
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined')
    subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);

  fab.hidden = false;
  update();
})();

// Reveal on scroll: product cards that start below the fold fade and rise in, a short stagger apart.
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches || !('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode))
    return;

  const SELECTOR = '.brand-card, .card-wrapper';
  const targets = Array.from(document.querySelectorAll(SELECTOR)).filter(
    (element) => element.getBoundingClientRect().top > window.innerHeight,
  );
  if (!targets.length) return;

  const finish = (element) => {
    element.classList.remove('kvs-reveal', 'is-revealed');
    element.style.removeProperty('transition-delay');
  };

  const reveal = (element, delay) => {
    element.style.transitionDelay = `${delay}ms`;
    element.classList.add('is-revealed');
    setTimeout(() => finish(element), 1000 + delay);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .forEach((entry, index) => {
          observer.unobserve(entry.target);
          reveal(entry.target, Math.min(index, 5) * 70);
        });
    },
    { rootMargin: '0px 0px -8% 0px' },
  );

  targets.forEach((element) => {
    element.classList.add('kvs-reveal');
    observer.observe(element);
  });

  // Safety net: nothing may stay hidden, for example inside a tab that is never scrolled into view.
  setTimeout(() => {
    observer.disconnect();
    targets.forEach(
      (element) =>
        element.classList.contains('kvs-reveal') && !element.classList.contains('is-revealed') && reveal(element, 0),
    );
  }, 8000);
})();

